import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Divider,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Toolbar,
  Tooltip,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
// Removed unused Teacher context import for cleaner build
import apiAxiosInstance from '../../utils/axiosInstance';
import ReactMarkdown from 'react-markdown';
import VideoHelpButton from '../Common/VideoHelpButton';
import html2pdf from 'html2pdf.js';

// Interface definitions
interface Question {
  question: string;
  answers: string[];
  correctAnswer: string;
  explanation?: string;
  standardReference?: string;
  bloomsLevel?: string;
}

interface AssessmentPassage {
  _id: string;
  title: string;
  genre: string;
  passage: string;
  isAIGenerated: boolean;
  includeAnswerKey: boolean;
  questions: Question[];
  passage2Questions?: Question[];
  combinedQuestions?: Question[];
  createdAt: string;
  isPairedPassage?: boolean;
  passages?: Array<{
    topic: string;
    title?: string;
    content: string;
    genre?: string;
  }>;
  gradeLevel: string;
  standards?: Array<{
    id: string;
    code: string;
    standard: string;
    description: string;
  }>;
  answerKey?: string;
}

interface ViewDialogProps {
  open: boolean;
  passage: AssessmentPassage | null;
  onClose: () => void;
}

// Component for displaying individual questions in the view dialog
const QuestionDisplay: React.FC<{ 
  question: Question; 
  index: number; 
  showAnswerKey: boolean; 
}> = ({ question, index, showAnswerKey }) => (
  <Box sx={{ mb: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
    <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
      {index + 1}. {question.question}
    </Typography>
    {question.answers.map((answer, answerIndex) => (
      <Typography key={answerIndex} variant="body2" sx={{ ml: 2, mb: 0.5 }}>
        {String.fromCharCode(65 + answerIndex)}. {answer}
      </Typography>
    ))}
    {showAnswerKey && (
      <Box sx={{ mt: 1, p: 1, backgroundColor: 'success.light', borderRadius: 0.5 }}>
        <Typography variant="body2" color="success.dark">
          <strong>Correct Answer:</strong> {question.correctAnswer}
        </Typography>
        {question.explanation && (
          <Typography variant="body2" color="success.dark">
            <strong>Explanation:</strong> {question.explanation}
          </Typography>
        )}
        {question.standardReference && (
          <Typography variant="body2" color="success.dark">
            <strong>Standard:</strong> {question.standardReference}
          </Typography>
        )}
      </Box>
    )}
  </Box>
);

// Dialog component for viewing passage details
const ViewPassageDialog: React.FC<ViewDialogProps> = ({ open, passage, onClose }) => {
  const [downloadAnchorEl, setDownloadAnchorEl] = React.useState<null | HTMLElement>(null);

  if (!passage) return null;

  const getTotalQuestionCount = () => {
    let count = 0;
    if (passage.questions) count += passage.questions.length;
    if (passage.passage2Questions) count += passage.passage2Questions.length;
    if (passage.combinedQuestions) count += passage.combinedQuestions.length;
    return count;
  };

  const handleDownloadClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setDownloadAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setDownloadAnchorEl(null);
  };

  const createPrintContent = (
    current: AssessmentPassage,
    format: 'print' | 'pdf' | 'word' = 'print'
  ) => {
    const normalizePassageEntries = () => {
      const entries: Array<{
        title: string;
        content: string;
        questions: any[];
      }> = [];

      const getTitle = (p: any) => p?.title || p?.topic || current.title || 'Untitled';
      const getContent = (p: any) => p?.passage || p?.content || current.passage || '';

      if (current.isPairedPassage && Array.isArray(current.passages) && current.passages.length >= 2) {
        const p0 = current.passages[0] as any;
        const p1 = current.passages[1] as any;
        const q0 = Array.isArray(current.questions)
          ? current.questions
          : Array.isArray(p0?.questions)
            ? p0.questions
            : [];
        const q1 = Array.isArray(current.passage2Questions)
          ? current.passage2Questions
          : Array.isArray(p1?.questions)
            ? p1.questions
            : [];
        entries.push({ title: getTitle(p0), content: getContent(p0), questions: q0 });
        entries.push({ title: getTitle(p1), content: getContent(p1), questions: q1 });
      } else {
        // Single passage
        const p0 = (Array.isArray(current.passages) && current.passages[0]) || ({} as any);
        const q0 = Array.isArray(current.questions)
          ? current.questions
          : Array.isArray((p0 as any)?.questions)
            ? (p0 as any).questions
            : [];
        entries.push({ title: getTitle(p0), content: getContent(p0), questions: q0 });
      }

      return entries;
    };

    const formatPassageContent = (content: string, title: string) => {
      let cleanContent = content || '';
      const safeTitle = (title || '').replace(/[.*+?^${}()|[\]\\]/g, '\$&');
      const titleRegex = new RegExp(`^(#+\\s*)?${safeTitle}\\s*\\n*`, 'i');
      cleanContent = cleanContent.replace(titleRegex, '').trim();

      return cleanContent
        .split(/\n\s*\n/)
        .filter(para => para.trim())
        .map(paragraph => `<p>${paragraph.trim()}</p>`) // simple paragraphs
        .join('\n');
    };

    const formatQuestions = (questions: any[], sectionTitle: string, startingNumber: number) => {
      let questionNumber = startingNumber;
      return `
        <div class="questions-section">
          <h2>${sectionTitle}</h2>
          ${questions?.map((q: any) => {
            const currentNumber = questionNumber++;
            const answersArray = Array.isArray(q?.answers) ? q.answers : [];
            return `
              <div class="question-block" style="margin-bottom: 2em;">
                <p class="question-text"><strong>${currentNumber}. ${q?.question || ''}</strong></p>
                <div class="answer-choices" style="margin-left: 1em; margin-top: 0.5em;">
                  ${answersArray.map((answer: any, j: number) => {
                    const cleanAnswer = typeof answer === 'object' && answer !== null && 'text' in answer
                      ? (answer as any).text
                      : String(answer ?? '');
                    return `
                      <div class="answer-choice" style="display: flex; margin-bottom: 0.5em; align-items: flex-start;">
                        <span class="answer-letter" style="font-weight: bold; margin-right: 0.3em; flex-shrink: 0;">${String.fromCharCode(65 + j)}.</span>
                        <span class="answer-text" style="flex: 1;">${cleanAnswer}</span>
                      </div>
                    `;
                  }).join('')}
                </div>
              </div>
            `;
          }).join('')}
        </div>
      `;
    };

    const entries = normalizePassageEntries();
    const combined = Array.isArray(current.combinedQuestions) ? current.combinedQuestions : [];

    let content = '';
    let questionCounter = 0;

    if (format === 'word') {
      content = '<div class="WordSection1">';
      entries.forEach((entry, index) => {
        const cleanTitle = (entry.title || '').replace(/^#+\s*/, '').trim();
        content += `
          <div class="passage-section">
            <h1 class="passage-title">${cleanTitle}</h1>
            <div class="passage-content">
              ${formatPassageContent(entry.content, entry.title)}
            </div>
            ${entry.questions && entry.questions.length > 0
              ? formatQuestions(entry.questions, index === 0 ? 'Questions' : `Questions for ${entry.title}`, questionCounter + 1)
              : ''}
          </div>
        `;
        questionCounter += entry.questions?.length || 0;
      });

      if (combined.length > 0) {
        content += formatQuestions(combined, 'Combined Questions', questionCounter + 1);
        questionCounter += combined.length;
      }

      if (current.includeAnswerKey && current.answerKey) {
        content += `
          <div class="answer-key-section">
            <h2>Complete Answer Key</h2>
            <pre style="white-space: pre-wrap; font-family: inherit;">${current.answerKey}</pre>
          </div>
        `;
      }

      content += '</div>';
    } else {
      content = '<div class="markdown-body">';
      entries.forEach((entry, index) => {
        const cleanTitle = (entry.title || '').replace(/^#+\s*/, '').trim();
        content += `
          <div class="passage-section ${index > 0 ? 'next-passage' : ''}">
            <h1 class="passage-title">${cleanTitle}</h1>
            <div class="passage-content">
              ${formatPassageContent(entry.content, entry.title)}
            </div>
            ${entry.questions && entry.questions.length > 0
              ? formatQuestions(entry.questions, index === 0 ? 'Questions' : `Questions for ${entry.title}`, questionCounter + 1)
              : ''}
          </div>
        `;
        questionCounter += entry.questions?.length || 0;
      });

      if (combined.length > 0) {
        content += formatQuestions(combined, 'Combined Questions', questionCounter + 1);
        questionCounter += combined.length;
      }

      if (current.includeAnswerKey && current.answerKey) {
        content += `
          <div class="answer-key-section">
            <h2>Complete Answer Key</h2>
            <pre style="white-space: pre-wrap; font-family: inherit;">${current.answerKey}</pre>
          </div>
        `;
      }

      content += '</div>';
    }

    return content;
  };

  const handlePrint = () => {
    if (!passage) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const content = createPrintContent(passage, 'print');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Assessment Passage</title>
          <style>
            body { font-family: 'Times New Roman', serif; font-size: 14px; line-height: 1.6; }
            .passage-title { text-align: center; font-size: 18px; font-weight: bold; margin-bottom: 1.5em; color: #2c5282; }
            .passage-content p { margin-bottom: 1.5em; text-indent: 2em; text-align: justify; }
            .questions-section { page-break-inside: avoid; }
            .questions-section h2 { 
              font-size: 16px; 
              margin: 2em 0 1.2em 0; 
              text-align: center; 
              color: #2c5282; 
              page-break-after: avoid;
              page-break-inside: avoid;
            }
            .question-text { 
              margin-bottom: 1em; 
              font-weight: bold; 
              page-break-after: avoid;
              page-break-inside: avoid;
            }
            .answer-letter { 
              font-weight: bold; 
              margin: 0; 
              padding: 0;
              display: inline;
            }
            .answer-text { 
              margin: 0 0 0.5em 0; 
              padding: 0;
              word-wrap: break-word;
              overflow-wrap: break-word;
              max-width: 100%;
            }
            @media print {
              @page { margin: 0.5in; }
              h1 { margin-top: 0; }
              .questions-section { page-break-before: always; }
              .question, .answer-choices, .answer-choice { page-break-inside: avoid !important; }
            }
          </style>
        </head>
        <body>
          ${content}
          <script>
            window.onload = function() {
              window.print();
            }
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
  };

  const handleDownloadPDF = async () => {
    if (!passage) return;
    const content = createPrintContent(passage, 'pdf');
    const element = document.createElement('div');
    element.innerHTML = `
      <style>
        body { font-family: 'Times New Roman', serif; font-size: 14px; line-height: 1.6; }
        .passage-title { text-align: center; font-size: 18px; font-weight: bold; margin-bottom: 1.5em; color: #2c5282; }
        .passage-content p { margin-bottom: 1.5em; text-indent: 2em; text-align: justify; }
        .questions-section { page-break-inside: avoid; }
        .questions-section h2 { 
          font-size: 16px; 
          margin: 2em 0 1.2em 0; 
          text-align: center; 
          color: #2c5282; 
          page-break-after: avoid;
          page-break-inside: avoid;
        }
        .question-text { 
          margin-bottom: 1em; 
          font-weight: bold; 
          page-break-after: avoid;
          page-break-inside: avoid;
        }
        .answer-letter { 
          font-weight: bold; 
          margin: 0; 
          padding: 0;
          display: inline;
        }
        .answer-text { 
          margin: 0 0 0.5em 0; 
          padding: 0;
          word-wrap: break-word;
          overflow-wrap: break-word;
          max-width: 100%;
        }
      </style>
      ${content}
    `;
    element.style.padding = '20px';
    element.className = 'pdf-container';

    const opt = {
      margin: 0.7,
      filename: 'passage.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        logging: false,
        letterRendering: true,
        windowWidth: 1200,
        scrollY: -window.scrollY,
        allowTaint: true
      },
      jsPDF: { 
        unit: 'in', 
        format: 'letter', 
        orientation: 'portrait',
        compress: true,
        hotfixes: ['px_scaling'],
        precision: 16
      },
      pagebreak: { 
        mode: ['css', 'legacy', 'avoid-all'],
        before: ['.questions-section'],
        avoid: [
          '.passage-section',
          '.question-block',
          '.answer-choices',
          '.question-wrapper',
          '.answer-wrapper',
          'h1',
          'h2',
          'h3',
          'h4',
          'h5',
          'h6',
          'p',
          'li'
        ]
      }
    } as any;

    try {
      await new Promise(resolve => setTimeout(resolve, 100));
      await (html2pdf() as any).set(opt).from(element).save();
      handleDownloadClose();
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const handleDownloadWord = () => {
    if (!passage) return;
    const content = createPrintContent(passage, 'word');
    const formattedContent = `
      <html xmlns:w="urn:schemas-microsoft-com:office:word"
            xmlns:v="urn:schemas-microsoft-com:vml"
            xmlns:o="urn:schemas-microsoft-com:office:office">
        <head>
          <meta charset="utf-8">
          <title>Assessment Passage</title>
          <style>
            body { font-family: 'Times New Roman', serif; font-size: 12pt; line-height: 1.6; }
            .passage-title { text-align: center; font-size: 16pt; font-weight: bold; margin-bottom: 1.5em; color: #2c5282; }
            .passage-content p { margin-bottom: 1.5em; text-indent: 2em; text-align: justify; }
            .questions-section { page-break-inside: avoid; }
            .questions-section h2 { 
              font-size: 14pt; 
              margin: 2em 0 1.2em 0; 
              text-align: center; 
              color: #2c5282; 
              page-break-after: avoid;
              page-break-inside: avoid;
            }
            .question-text { 
              margin-bottom: 1em; 
              font-weight: bold; 
              page-break-after: avoid;
              page-break-inside: avoid;
            }
            .answer-letter { 
              font-weight: bold; 
              margin: 0; 
              padding: 0;
              display: inline;
            }
            .answer-text { 
              margin: 0 0 0.5em 0; 
              padding: 0;
              word-wrap: break-word;
              overflow-wrap: break-word;
              max-width: 100%;
            }
          </style>
        </head>
        <body>
          ${content}
        </body>
      </html>
    `;

    const blob = new Blob([formattedContent], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'passage.doc';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    handleDownloadClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
          <Typography variant="h6" component="div">
            {passage.isPairedPassage && passage.passages ? 
              `Paired: ${passage.passages[0]?.topic || 'Passage 1'} & ${passage.passages[1]?.topic || 'Passage 2'}` :
              passage.title || 'Untitled Passage'
            }
          </Typography>
          {passage.isPairedPassage && (
            <Chip 
              label="Paired Passage" 
              size="small" 
              color="primary" 
              variant="outlined"
            />
          )}
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Genre: {passage.genre} • Grade Level: {passage.gradeLevel} • Total Questions: {getTotalQuestionCount()}
            {passage.standards && passage.standards.length > 0 && (
              <>
                {' • '}
                Standards: {passage.standards.map(std => std.code).join(', ')}
              </>
            )}
          </Typography>
        </Box>

        {passage.isPairedPassage && passage.passages && passage.passages.length === 2 ? (
          <>
            {/* First Passage */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                Passage 1: {passage.passages[0]?.topic || 'Untitled'}
              </Typography>
              <Paper elevation={1} sx={{ p: 2, backgroundColor: 'grey.50' }}>
                <ReactMarkdown>
                  {passage.passages[0]?.content || 'No content available'}
                </ReactMarkdown>
              </Paper>
              
              {passage.questions && passage.questions.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Questions for Passage 1
                  </Typography>
                  {passage.questions.map((question, index) => (
                    <QuestionDisplay 
                      key={index} 
                      question={question} 
                      index={index} 
                      showAnswerKey={passage.includeAnswerKey} 
                    />
                  ))}
                </Box>
              )}
            </Box>

            {/* Second Passage */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                Passage 2: {passage.passages[1]?.topic || 'Untitled'}
              </Typography>
              <Paper elevation={1} sx={{ p: 2, backgroundColor: 'grey.50' }}>
                <ReactMarkdown>
                  {passage.passages[1]?.content || 'No content available'}
                </ReactMarkdown>
              </Paper>
              
              {passage.passage2Questions && passage.passage2Questions.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Questions for Passage 2
                  </Typography>
                  {passage.passage2Questions.map((question, index) => (
                    <QuestionDisplay 
                      key={index} 
                      question={question} 
                      index={index + (passage.questions?.length || 0)} 
                      showAnswerKey={passage.includeAnswerKey} 
                    />
                  ))}
                </Box>
              )}
            </Box>

            {/* Combined Questions */}
            {passage.combinedQuestions && passage.combinedQuestions.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Combined Questions
                </Typography>
                {passage.combinedQuestions.map((question, index) => (
                  <QuestionDisplay 
                    key={index} 
                    question={question} 
                    index={index + (passage.questions?.length || 0) + (passage.passage2Questions?.length || 0)} 
                    showAnswerKey={passage.includeAnswerKey} 
                  />
                ))}
              </Box>
            )}
          </>
        ) : (
          <>
            {/* Single Passage */}
            <Paper elevation={1} sx={{ p: 2, backgroundColor: 'grey.50', mb: 3 }}>
              <ReactMarkdown>
                {passage.passage || 'No content available'}
              </ReactMarkdown>
            </Paper>
            
            {/* Single passage questions */}
            {passage.questions && passage.questions.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Questions
                </Typography>
                {passage.questions.map((question, index) => (
                  <QuestionDisplay 
                    key={index} 
                    question={question} 
                    index={index} 
                    showAnswerKey={passage.includeAnswerKey} 
                  />
                ))}
              </Box>
            )}
          </>
        )}

        {/* Answer Key */}
        {passage.includeAnswerKey && passage.answerKey && (
          <Box sx={{ mt: 4 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Complete Answer Key
            </Typography>
            <Paper elevation={1} sx={{ p: 2, backgroundColor: 'grey.50' }}>
              <Typography
                variant="body2"
                component="pre"
                sx={{
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'inherit',
                }}
              >
                {passage.answerKey}
              </Typography>
            </Paper>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Print
        </Button>
        <Button
          startIcon={<DownloadIcon />}
          aria-controls="view-download-menu"
          aria-haspopup="true"
          onClick={handleDownloadClick}
        >
          Download
        </Button>
        <Menu
          anchorEl={downloadAnchorEl}
          open={Boolean(downloadAnchorEl)}
          onClose={handleDownloadClose}
          id="view-download-menu"
        >
          <MenuItem onClick={handleDownloadPDF}>
            <PictureAsPdfIcon style={{ marginRight: 8 }} />
            Download as PDF
          </MenuItem>
          <MenuItem onClick={handleDownloadWord}>
            <DescriptionIcon style={{ marginRight: 8 }} />
            Download as Word
          </MenuItem>
        </Menu>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// Main PassagesList component
const PassagesList: React.FC = () => {
  // No teacher context needed on this screen
  const [passages, setPassages] = useState<AssessmentPassage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPassage, setSelectedPassage] = useState<AssessmentPassage | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  useEffect(() => {
    fetchPassages();
  }, []);

  const fetchPassages = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching passages...');
      const response = await apiAxiosInstance.get('/api/assessment-passages');
      console.log('Response:', response.data);
      setPassages(response.data);
    } catch (err) {
      console.error('Error fetching passages:', err);
      setError('Failed to load passages. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPassage = (passage: AssessmentPassage) => {
    setSelectedPassage(passage);
    setViewDialogOpen(true);
  };

  const handleDeletePassage = async (passageId: string) => {
    if (!window.confirm('Are you sure you want to delete this passage? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleteLoading(passageId);
      await apiAxiosInstance.delete(`/api/assessment-passages/${passageId}`);
      setSnackbar({ open: true, message: 'Passage deleted successfully', severity: 'success' });
      
      // Remove the deleted passage from the local state
      setPassages(prev => prev.filter(passage => passage._id !== passageId));
    } catch (err) {
      console.error('Error deleting passage:', err);
      setSnackbar({ open: true, message: 'Failed to delete passage. Please try again.', severity: 'error' });
    } finally {
      setDeleteLoading(null);
    }
  };

  const allSelected = useMemo(() => {
    return passages.length > 0 && selectedIds.length === passages.length;
  }, [selectedIds, passages]);

  const someSelected = useMemo(() => {
    return selectedIds.length > 0 && selectedIds.length < passages.length;
  }, [selectedIds, passages]);

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(passages.map(p => p._id));
    }
  };

  const toggleSelectOne = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const openBulkDelete = () => {
    if (selectedIds.length === 0) return;
    setBulkDeleteOpen(true);
  };

  const handleConfirmBulkDelete = async () => {
    setIsBulkDeleting(true);
    try {
      const idsToDelete = [...selectedIds];
      const results = await Promise.allSettled(
        idsToDelete.map(id => apiAxiosInstance.delete(`/api/assessment-passages/${id}`))
      );

      const successCount = results.filter(r => r.status === 'fulfilled').length;
      const failedCount = results.length - successCount;

      if (successCount > 0) {
        setPassages(prev => prev.filter(p => !idsToDelete.includes(p._id)));
      }
      setSelectedIds([]);
      setSnackbar({
        open: true,
        message: failedCount === 0 ? `Deleted ${successCount} passage(s)` : `Deleted ${successCount} passage(s); ${failedCount} failed`,
        severity: failedCount === 0 ? 'success' : 'error'
      });
    } catch (err) {
      console.error('Bulk delete error:', err);
      setSnackbar({ open: true, message: 'Failed to delete selected passages.', severity: 'error' });
    } finally {
      setIsBulkDeleting(false);
      setBulkDeleteOpen(false);
    }
  };

  const getTotalQuestionCount = (passage: AssessmentPassage) => {
    let count = 0;
    if (passage.questions) count += passage.questions.length;
    if (passage.passage2Questions) count += passage.passage2Questions.length;
    if (passage.combinedQuestions) count += passage.combinedQuestions.length;
    return count;
  };

  const formatDate = (dateString: string) => {
    // Short, date-only format (e.g., Aug 11, 2025)
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
          Passage Library
        </Typography>
        <VideoHelpButton
          url="https://www.youtube.com/watch?v=qlaxI0613Vw"
          title="Passage List"
          buttonLabel="Watch tutorial"
          color="secondary"
          emphasis="solid"
        />
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Manage your saved reading passages and assessments
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Bulk actions toolbar */}
      <Toolbar sx={{ pl: { sm: 0 }, pr: { xs: 1, sm: 1 }, justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2">
          {selectedIds.length > 0 ? `${selectedIds.length} selected` : `${passages.length} item(s)`}
        </Typography>
        <Tooltip title={selectedIds.length === 0 ? 'Select rows to enable' : 'Delete selected'}>
          <span>
            <Button
              variant="contained"
              color="error"
              disabled={selectedIds.length === 0}
              onClick={openBulkDelete}
              startIcon={<DeleteIcon />}
            >
              Delete Selected
            </Button>
          </span>
        </Tooltip>
      </Toolbar>

      {passages.length === 0 ? (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Passages Found
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You haven't created any passages yet. Start by creating your first passage using the assessment tool.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table aria-label="passages table" size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: 48 }}>
                  <Checkbox
                    indeterminate={someSelected}
                    checked={allSelected}
                    onChange={toggleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ width: '40%' }}>Title</TableCell>
                <TableCell sx={{ width: '18%' }}>Genre</TableCell>
                <TableCell sx={{ width: 80, textAlign: 'center' }}>Grade</TableCell>
                <TableCell sx={{ width: 110, textAlign: 'center' }}>Questions</TableCell>
                <TableCell sx={{ width: 140 }}>Created</TableCell>
                <TableCell align="right" sx={{ width: 200, whiteSpace: 'nowrap' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {passages.map((passage) => {
                const isChecked = selectedIds.includes(passage._id);
                const isPaired = Boolean(passage.isPairedPassage && passage.passages && passage.passages.length >= 2);
                const titlePrimary = isPaired ? (passage.passages?.[0]?.topic || 'Passage 1') : (passage.title || 'Untitled Passage');
                const titleSecondary = isPaired ? (passage.passages?.[1]?.topic || 'Passage 2') : '';
                const g1 = isPaired ? (passage.passages?.[0]?.genre || passage.genre) : passage.genre;
                const g2 = isPaired ? (passage.passages?.[1]?.genre || passage.genre) : '';
                return (
                  <TableRow key={passage._id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox checked={isChecked} onChange={() => toggleSelectOne(passage._id)} />
                    </TableCell>
                    <TableCell>
                      {isPaired ? (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                              {titlePrimary}
                            </Typography>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                              {titleSecondary}
                            </Typography>
                          </Box>
                          <Chip label="Paired" size="small" color="primary" variant="outlined" />
                        </Box>
                      ) : (
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{titlePrimary}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: isPaired ? 'pre-wrap' : 'normal' }}>
                      {isPaired ? (
                        <>
                          <Typography variant="body2">{g1}</Typography>
                          <Typography variant="body2">{g2}</Typography>
                        </>
                      ) : (
                        passage.genre
                      )}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>{passage.gradeLevel}</TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {getTotalQuestionCount(passage)}
                    </TableCell>
                    <TableCell>{formatDate(passage.createdAt)}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, flexWrap: 'nowrap' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleViewPassage(passage)}
                        >
                          View
                        </Button>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeletePassage(passage._id)}
                          disabled={deleteLoading === passage._id}
                          sx={{ '&:hover': { backgroundColor: 'error.light', color: 'white' } }}
                        >
                          {deleteLoading === passage._id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* View Dialog */}
      <ViewPassageDialog
        open={viewDialogOpen}
        passage={selectedPassage}
        onClose={() => {
          setViewDialogOpen(false);
          setSelectedPassage(null);
        }}
      />

      {/* Snackbar for success/error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />

      {/* Bulk delete confirmation */}
      <Dialog open={bulkDeleteOpen} onClose={() => setBulkDeleteOpen(false)}>
        <DialogTitle>Delete Selected Passages</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedIds.length} selected passage(s)? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteOpen(false)} disabled={isBulkDeleting}>Cancel</Button>
          <Button color="error" onClick={handleConfirmBulkDelete} disabled={isBulkDeleting} startIcon={isBulkDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}>
            {isBulkDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PassagesList; 